import { useEffect, useState } from "react";
import { Divider, Drawer, Empty, Flex, Spin, Typography } from "antd";
import { TruckOutlined } from "@ant-design/icons";
import { BillingAgendaIcon } from "@/components/icon/BillingAgendaIcon";
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { fromV1 } from "@/pages/contacts/Address/agenda.adapter";

const { Text } = Typography;

interface Address {
  userAddressId: number;
  firstname: string;
  lastname: string;
  telephone: string;
  street: string;
  city: string;
  region: string;
  postcode: string;
  company: string;
  vatId: string;
  shippingIndication: string;
  billingType?: { label: string };
  stores?: { code: string };
  addressType: { shipping: boolean; billing: boolean };
}

interface StoreAddresses {
  billing: Address[];
  delivery: Address[];
}

export const AgendaPanel = ({
  open,
  onClose,
  userId,
  storeCode,
}: {
  open: boolean;
  onClose: () => void;
  userId?: number;
  storeCode: string;
}) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    let active = true;
    setLoading(true);
    const params: Record<string, string | number> = { user_id: userId };
    if (storeCode) params.store = storeCode;
    axiosInstance
      .get(`${API_BASE_URL}/addresses`, { params })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        setAddresses(rows.map(fromV1));
      })
      .catch(() => active && setAddresses([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [open, userId, storeCode]);

  const groupedByStore = addresses.reduce<Record<string, StoreAddresses>>((acc, addr) => {
    const code = addr.stores?.code ?? "General";
    if (!acc[code]) acc[code] = { billing: [], delivery: [] };
    if (addr.addressType.billing) acc[code].billing.push(addr);
    if (addr.addressType.shipping) acc[code].delivery.push(addr);
    return acc;
  }, {});

  const billingByStore = Object.entries(groupedByStore).filter(([, a]) => a.billing.length > 0);
  const deliveryByStore = Object.entries(groupedByStore).filter(([, a]) => a.delivery.length > 0);

  const locationLine = (addr: Address) =>
    [addr.city, addr.region, addr.postcode ? `(${addr.postcode})` : null]
      .filter(Boolean)
      .join(", ");

  const billingCard = (addr: Address) => (
    <Flex vertical key={addr.userAddressId} gap={2}>
      {addr.billingType?.label && <Text>{addr.billingType.label}</Text>}
      {(addr.company || `${addr.firstname} ${addr.lastname}`.trim()) && (
        <Text>{addr.company || `${addr.firstname} ${addr.lastname}`.trim()}</Text>
      )}
      {addr.vatId && <Text>{addr.vatId}</Text>}
      {addr.telephone && <Text>{addr.telephone}</Text>}
      {addr.street && <Text>{addr.street}</Text>}
      {locationLine(addr) && <Text>{locationLine(addr)}</Text>}
      {addr.shippingIndication && <Text>{addr.shippingIndication}</Text>}
    </Flex>
  );

  const deliveryCard = (addr: Address) => (
    <Flex vertical key={addr.userAddressId} gap={2}>
      {`${addr.firstname} ${addr.lastname}`.trim() && (
        <Text>{`${addr.firstname} ${addr.lastname}`.trim()}</Text>
      )}
      {addr.telephone && <Text>{addr.telephone}</Text>}
      {addr.street && <Text>{addr.street}</Text>}
      {locationLine(addr) && <Text>{locationLine(addr)}</Text>}
      {addr.shippingIndication && <Text>{addr.shippingIndication}</Text>}
    </Flex>
  );

  return (
    <Drawer title="Agenda" placement="right" width={400} open={open} onClose={onClose}>
      {loading ? (
        <div style={{ textAlign: "center", paddingTop: 40 }}>
          <Spin />
        </div>
      ) : addresses.length === 0 ? (
        <Empty description="Sin direcciones cargadas" />
      ) : (
        <Flex vertical gap={20}>
          {/* Billing agenda */}
          <Flex vertical gap={16}>
            <Flex align="center" gap={8}>
              <BillingAgendaIcon style={{ fontSize: 16 }} />
              <Text strong>Agenda de facturación</Text>
            </Flex>
            {billingByStore.length === 0 ? (
              <Text type="secondary" style={{ marginLeft: 22, fontSize: 13 }}>
                Sin direcciones de facturación
              </Text>
            ) : (
              billingByStore.flatMap(([, addrs]) =>
                addrs.billing.map((addr, i) => (
                  <Flex vertical key={addr.userAddressId} gap={0}>
                    {i > 0 && <Divider style={{ margin: "8px 0" }} />}
                    {billingCard(addr)}
                  </Flex>
                ))
              )
            )}
          </Flex>

          <Divider style={{ margin: 0 }} />

          {/* Delivery agenda */}
          <Flex vertical gap={16}>
            <Flex align="center" gap={8}>
              <TruckOutlined />
              <Text strong>Agenda de entrega</Text>
            </Flex>
            {deliveryByStore.length === 0 ? (
              <Text type="secondary" style={{ marginLeft: 22, fontSize: 13 }}>
                Sin direcciones de entrega
              </Text>
            ) : (
              deliveryByStore.flatMap(([, addrs]) =>
                addrs.delivery.map((addr, i) => (
                  <Flex vertical key={addr.userAddressId} gap={0}>
                    {i > 0 && <Divider style={{ margin: "8px 0" }} />}
                    {deliveryCard(addr)}
                  </Flex>
                ))
              )
            )}
          </Flex>

          <Divider style={{ margin: 0 }} />

          <Text type="secondary" italic style={{ fontSize: 12 }}>
            Si quieres cambiar direcciones de entrega o facturación de este Contacto debes
            realizarlo desde la ficha del Cliente en la sección de agendas.
          </Text>
        </Flex>
      )}
    </Drawer>
  );
};
